import React, { useEffect } from 'react';
import "./App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "components/ui/sonner";
import useAuthStore from "stores/authStore";
import { subscribeToPush } from "services/pushNotifications";

// Pages
import LandingPage from "pages/LandingPage";
import LoginPage from "pages/LoginPage";
import RegisterPage from "pages/RegisterPage";
import AuthCallback from "pages/AuthCallback";
import RoleSelection from "pages/RoleSelection";
import Dashboard from "pages/Dashboard";
import CareProfileSetup from "pages/CareProfileSetup";
import CaregiverProfileSetup from "pages/CaregiverProfileSetup";
import CareGuide from "pages/CareGuide";
import CareCircle from "pages/CareCircle";
import CaregiverDirectory from "pages/CaregiverDirectory";
import CaregiverProfile from "pages/CaregiverProfile";
import CareHours from "pages/CareHours";
import StressCheckin from "pages/StressCheckin";
import Subscription from "pages/Subscription";
import SubscriptionSuccess from "pages/SubscriptionSuccess";
import Settings from "pages/Settings";
import AdminDashboard from "pages/AdminDashboard";
// import BudgetDashboard from "pages/BudgetDashboard";
// import Payroll from "pages/Payroll";
import ResourceCenter from "pages/ResourceCenter";
import QuickEnroll from "pages/QuickEnroll";
import Certifications from "pages/Certifications";
import AvailabilityCalendar from "pages/AvailabilityCalendar";
import Support from "pages/Support";
import Messages from "pages/Messages";
import VendorDashboard from "pages/VendorDashboard";
import AgencyDashboard from "pages/AgencyDashboard";
import CaseManagerDashboard from "pages/CaseManagerDashboard";
import Medications from "pages/Medications";
import HealthMetrics from "pages/HealthMetrics";
import FamilyExpenses from "pages/FamilyExpenses";
import ClientDirectory from "pages/ClientDirectory";
import TimeOffRequests from "pages/TimeOffRequests";
import EVVCompliance from "pages/EVVCompliance";
import Analytics from "pages/Analytics";
import SMSSettings from "pages/SMSSettings";
import HIPAAGuide from "pages/HIPAAGuide";
import ShiftMonitoring from "pages/ShiftMonitoring";
import BackupShifts from "pages/BackupShifts";
import HealthAlerts from "pages/HealthAlerts";
import VerifyEmail from "pages/VerifyEmail";
import ForgotPassword from "pages/ForgotPassword";
import ResetPassword from "pages/ResetPassword";
import EmailVerificationBanner from "components/EmailVerificationBanner";
import DailyChecklist from "pages/DailyChecklist";
import NotificationPreferences from "pages/NotificationPreferences";
import Notifications from "pages/Notifications";
import PrivacyPolicy from "pages/PrivacyPolicy";
import TermsOfService from "pages/TermsOfService";

// Auth initializer — runs checkAuth once on mount
function AuthInit({ children }) {
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user && 'Notification' in window) {
      subscribeToPush();
    }
  }, [user]);

  return children;
}

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/role-selection" element={<RoleSelection />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/care-profile/setup" element={<ProtectedRoute><CareProfileSetup /></ProtectedRoute>} />
      <Route path="/caregiver-profile/setup" element={<ProtectedRoute><CaregiverProfileSetup /></ProtectedRoute>} />
      <Route path="/care-guide" element={<ProtectedRoute><CareGuide /></ProtectedRoute>} />
      <Route path="/care-circle" element={<ProtectedRoute><CareCircle /></ProtectedRoute>} />
      <Route path="/caregivers" element={<ProtectedRoute allowedRoles={['family_caregiver', 'admin']}><CaregiverDirectory /></ProtectedRoute>} />
      <Route path="/caregivers/:userId" element={<ProtectedRoute allowedRoles={['family_caregiver', 'admin']}><CaregiverProfile /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute allowedRoles={['independent_caregiver']}><ClientDirectory /></ProtectedRoute>} />
      <Route path="/care-hours" element={<ProtectedRoute><CareHours /></ProtectedRoute>} />
      <Route path="/stress-checkin" element={<ProtectedRoute><StressCheckin /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      {/* <Route path="/budget" element={<ProtectedRoute><BudgetDashboard /></ProtectedRoute>} /> */}
      {/* <Route path="/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} /> */}
      <Route path="/resources" element={<ProtectedRoute><ResourceCenter /></ProtectedRoute>} />
      <Route path="/quick-enroll" element={<ProtectedRoute allowedRoles={['family_caregiver']}><QuickEnroll /></ProtectedRoute>} />
      <Route path="/certifications" element={<ProtectedRoute allowedRoles={['independent_caregiver']}><Certifications /></ProtectedRoute>} />
      <Route path="/availability" element={<ProtectedRoute><AvailabilityCalendar /></ProtectedRoute>} />
      <Route path="/time-off" element={<ProtectedRoute allowedRoles={['family_caregiver', 'independent_caregiver']}><TimeOffRequests /></ProtectedRoute>} />
      <Route path="/evv" element={<ProtectedRoute allowedRoles={['family_caregiver', 'independent_caregiver']}><EVVCompliance /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute allowedRoles={['family_caregiver', 'independent_caregiver']}><Analytics /></ProtectedRoute>} />
      <Route path="/sms-settings" element={<ProtectedRoute><SMSSettings /></ProtectedRoute>} />
      <Route path="/resources/hipaa-guide" element={<ProtectedRoute><HIPAAGuide /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/shift-monitoring" element={<ProtectedRoute><ShiftMonitoring /></ProtectedRoute>} />
      <Route path="/backup-shifts" element={<ProtectedRoute><BackupShifts /></ProtectedRoute>} />
      <Route path="/health-alerts" element={<ProtectedRoute><HealthAlerts /></ProtectedRoute>} />
      <Route path="/daily-checklist" element={<ProtectedRoute><DailyChecklist /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/vendor" element={<ProtectedRoute allowedRoles={['vendor']}><VendorDashboard /></ProtectedRoute>} />
      <Route path="/agency" element={<ProtectedRoute allowedRoles={['agency']}><AgencyDashboard /></ProtectedRoute>} />
      <Route path="/case-manager" element={<ProtectedRoute allowedRoles={['case_manager']}><CaseManagerDashboard /></ProtectedRoute>} />
      <Route path="/medications" element={<ProtectedRoute><Medications /></ProtectedRoute>} />
      <Route path="/health-metrics" element={<ProtectedRoute><HealthMetrics /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><FamilyExpenses /></ProtectedRoute>} />
      <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppContent() {
  return (
    <>
      <EmailVerificationBanner />
      <AppRouter />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthInit>
        <Toaster position="top-right" richColors />
        <AppContent />
      </AuthInit>
    </BrowserRouter>
  );
}

export default App;
